export type SiteAccent = "coral" | "cyan" | "violet" | "mint" | "amber";
export type SiteDensity = "comfortable" | "compact";
export type MotionPreference = "system" | "full" | "reduced";

export interface AppearancePreferences {
  accent: SiteAccent;
  density: SiteDensity;
  motion: MotionPreference;
  profileEffects: boolean;
  pageTransitions: boolean;
}

export const APPEARANCE_KEY = "anivora.appearance.v1";

export const DEFAULT_APPEARANCE: AppearancePreferences = {
  accent: "coral",
  density: "comfortable",
  motion: "system",
  profileEffects: true,
  pageTransitions: true,
};

export const SITE_ACCENTS: Array<{ id: SiteAccent; color: string; label: string }> = [
  { id: "coral", color: "#f0445c", label: "Коралл" },
  { id: "cyan", color: "#0ea5e9", label: "Океан" },
  { id: "violet", color: "#8b5cf6", label: "Фиолетовый" },
  { id: "mint", color: "#14b8a6", label: "Мята" },
  { id: "amber", color: "#f59e0b", label: "Янтарь" },
];

const VALID_ACCENTS = new Set<SiteAccent>(SITE_ACCENTS.map(({ id }) => id));

export function readAppearancePreferences(): AppearancePreferences {
  try {
    const value = JSON.parse(localStorage.getItem(APPEARANCE_KEY) ?? "null") as Partial<AppearancePreferences> | null;
    if (!value || typeof value !== "object") return DEFAULT_APPEARANCE;
    return {
      accent: value.accent && VALID_ACCENTS.has(value.accent) ? value.accent : DEFAULT_APPEARANCE.accent,
      density: value.density === "compact" ? "compact" : "comfortable",
      motion: value.motion === "full" || value.motion === "reduced" ? value.motion : "system",
      profileEffects: typeof value.profileEffects === "boolean" ? value.profileEffects : true,
      pageTransitions: typeof value.pageTransitions === "boolean" ? value.pageTransitions : true,
    };
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

export function applyAppearancePreferences(preferences = readAppearancePreferences()): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.anivoraDensity = preferences.density;
  root.dataset.anivoraMotion = preferences.motion;
  root.dataset.anivoraEffects = preferences.profileEffects ? "on" : "off";
  root.dataset.anivoraTransitions = preferences.pageTransitions ? "on" : "off";

  const accent = SITE_ACCENTS.find(({ id }) => id === preferences.accent) ?? SITE_ACCENTS[0]!;
  for (const property of ["--primary", "--ring", "--sidebar-primary", "--sidebar-ring", "--chart-1", "--series-1"]) {
    root.style.setProperty(property, accent.color);
  }
}

export function saveAppearancePreferences(preferences: AppearancePreferences): void {
  try {
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(preferences));
  } catch {
    // Settings still apply for this page if browser storage is unavailable.
  }
  applyAppearancePreferences(preferences);
}
